package com.havn.app.features.auth.data

import com.havn.app.core.logging.HavnLogger
import com.havn.app.core.media.HavnMediaBucket
import com.havn.app.core.media.HavnPickedPhoto
import com.havn.app.core.media.havnFileExtension
import com.havn.app.core.media.havnPublicStorageSource
import com.havn.app.features.auth.domain.AgentProfile
import com.havn.app.features.auth.domain.AuthState
import com.havn.app.features.auth.domain.AuthRepository
import com.havn.app.features.auth.domain.AuthUser
import io.github.jan.supabase.annotations.SupabaseInternal
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.auth.OtpType
import io.github.jan.supabase.auth.auth
import io.github.jan.supabase.auth.providers.Apple
import io.github.jan.supabase.auth.providers.Google
import io.github.jan.supabase.auth.providers.builtin.IDToken
import io.github.jan.supabase.auth.providers.builtin.OTP
import io.github.jan.supabase.auth.status.SessionSource
import io.github.jan.supabase.auth.status.SessionStatus
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.postgrest.rpc
import io.github.jan.supabase.postgrest.query.Columns
import io.github.jan.supabase.storage.storage
import io.ktor.http.ContentType
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonPrimitive

class SupabaseAuthRepository(
    private val client: SupabaseClient,
    scope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
) : AuthRepository {
    private val agentProfileCache = mutableMapOf<String, AgentProfile>()
    private val agentProfileMutex = Mutex()

    override val authState: StateFlow<AuthState> = client.auth.sessionStatus
        .map { status ->
            when (status) {
                SessionStatus.Initializing -> AuthState.Loading
                is SessionStatus.Authenticated -> AuthState.LoggedIn(
                    AuthUser(
                        id = status.session.user?.id ?: "",
                        email = status.session.user?.email
                    )
                )
                is SessionStatus.NotAuthenticated -> AuthState.LoggedOut
                is SessionStatus.RefreshFailure -> AuthState.LoggedOut
            }
        }
        .stateIn(scope, SharingStarted.Eagerly, AuthState.Loading)

    @OptIn(SupabaseInternal::class)
    override suspend fun awaitSessionRestore() {
        client.auth.awaitInitialization()
        val restoredSession = try {
            client.auth.sessionManager.loadSessionOrNull()
        } catch (throwable: Throwable) {
            HavnLogger.error("Auth", "Stored session could not be restored", throwable)
            null
        }
        if (restoredSession != null) {
            client.auth.importSession(restoredSession, source = SessionSource.Storage)
        } else if (client.auth.sessionStatus.value is SessionStatus.Initializing) {
            client.auth.setSessionStatus(SessionStatus.NotAuthenticated())
        }
    }

    override suspend fun signInWithGoogle(): Result<Unit> {
        return runCatching {
            client.auth.signInWith(Google) {
                scopes.add("email")
                scopes.add("profile")
            }
        }.onFailure {
            HavnLogger.error("Auth", "Google sign-in failed", it)
        }
    }

    override suspend fun signInWithApple(): Result<Unit> {
        return runCatching {
            if (HavnAppleSignIn.isNativeAvailable) {
                val credential = HavnAppleSignIn.requestCredential()
                client.auth.signInWith(IDToken) {
                    idToken = credential.idToken
                    provider = Apple
                    nonce = credential.nonce
                }
            } else {
                client.auth.signInWith(Apple) {
                    scopes.add("email")
                    scopes.add("name")
                }
            }
        }.onFailure {
            HavnLogger.error("Auth", "Apple sign-in failed", it)
        }
    }

    override suspend fun sendEmailOtp(email: String): Result<Unit> {
        return runCatching {
            client.auth.signInWith(OTP) {
                this.email = email.trim()
                createUser = true
            }
        }.onFailure {
            HavnLogger.error("Auth", "Email OTP request failed for ${email.trim()}", it)
        }
    }

    override suspend fun agentExistsForEmail(email: String): Result<Boolean> {
        return runCatching {
            client.postgrest.rpc(
                function = "agent_exists_for_email",
                parameters = AgentExistsForEmailParams(email = email.trim())
            ).decodeAs<Boolean>()
        }.onFailure {
            HavnLogger.error("Auth", "Failed to check agent email ${email.trim()}", it)
        }
    }

    override suspend fun verifyEmailOtp(email: String, code: String): Result<Unit> {
        return runCatching {
            client.auth.verifyEmailOtp(
                type = OtpType.Email.EMAIL,
                email = email.trim(),
                token = code.trim()
            )
            client.auth.currentUserOrNull()?.let { user ->
                ensureAgentProfile(AuthUser(id = user.id, email = user.email)).getOrThrow()
            }
            Unit
        }.onFailure {
            HavnLogger.error("Auth", "Email OTP verification failed for ${email.trim()}", it)
        }
    }

    override suspend fun ensureAgentProfile(user: AuthUser): Result<Unit> {
        return runCatching {
            if (user.id.isBlank()) return@runCatching
            agentProfileCache[user.id]?.let { return@runCatching }
            agentProfileMutex.withLock {
                agentProfileCache[user.id]?.let { return@withLock }
                val existing = client.from(AGENTS)
                    .select(AgentProfileColumns) {
                        filter { eq("id", user.id) }
                        limit(1)
                    }
                    .decodeList<AgentProfileRow>()
                    .firstOrNull()
                if (existing != null) {
                    agentProfileCache[user.id] = existing.toDomain()
                    return@withLock
                }

                val currentUser = client.auth.currentUserOrNull()
                    ?: runCatching { client.auth.retrieveUserForCurrentSession(updateSession = true) }.getOrNull()
                val email = currentUser?.email ?: user.email.orEmpty()
                val metadata = currentUser?.userMetadata
                val fullName = metadata?.stringValue("full_name")
                    ?: metadata?.stringValue("name")
                    ?: email.substringBefore("@").replace(".", " ").replace("_", " ").trim()
                        .replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }
                        .ifBlank { "HAVN Agent" }
                val firstName = metadata?.stringValue("first_name")
                    ?: metadata?.stringValue("given_name")
                    ?: fullName.substringBefore(" ").ifBlank { fullName }
                val lastName = metadata?.stringValue("last_name")
                    ?: metadata?.stringValue("family_name")
                    ?: fullName.substringAfter(" ", "")

                client.from(AGENTS).insert(
                    AgentProfileInsertRow(
                        id = user.id,
                        firstName = firstName,
                        lastName = lastName,
                        fullName = fullName,
                        email = email
                    )
                )
                agentProfileCache[user.id] = AgentProfile(
                    id = user.id,
                    firstName = firstName,
                    lastName = lastName,
                    fullName = fullName,
                    email = email,
                    avatarDataUrl = null
                )
            }
            Unit
        }.onFailure {
            HavnLogger.error("Auth", "Failed to ensure agent profile ${user.id}", it)
        }
    }

    override suspend fun agentProfile(userId: String): Result<AgentProfile> {
        return runCatching {
            agentProfileCache[userId]?.let { return@runCatching it }
            agentProfileMutex.withLock {
                agentProfileCache[userId]?.let { return@withLock it }
                client.from(AGENTS)
                    .select(AgentProfileColumns) {
                        filter { eq("id", userId) }
                        limit(1)
                    }
                    .decodeSingle<AgentProfileRow>()
                    .toDomain()
                    .also { agentProfileCache[userId] = it }
            }
        }.onFailure {
            HavnLogger.error("Auth", "Failed to load agent profile $userId", it)
        }
    }

    override suspend fun updateAgentProfile(userId: String, firstName: String, lastName: String): Result<AgentProfile> {
        return runCatching {
            val normalizedFirstName = firstName.trim()
            val normalizedLastName = lastName.trim()
            val fullName = "$normalizedFirstName $normalizedLastName".trim()
            client.from(AGENTS).update(
                AgentProfileUpdateRow(
                    firstName = normalizedFirstName,
                    lastName = normalizedLastName,
                    fullName = fullName
                )
            ) {
                filter { eq("id", userId) }
                select()
            }.decodeSingle<AgentProfileRow>().toDomain()
                .also { agentProfileCache[userId] = it }
        }.onFailure {
            HavnLogger.error("Auth", "Failed to update agent profile $userId", it)
        }
    }

    override suspend fun updateAgentPhoto(userId: String, photo: HavnPickedPhoto): Result<AgentProfile> {
        return runCatching {
            require(photo.bytes.isNotEmpty()) { "Choose a non-empty profile photo." }
            val contentType = photo.contentType.ifBlank { "image/jpeg" }
            val safeName = photo.fileName.sanitizeFileName()
            val storagePath = "$userId/avatar/${timestampSlug()}-$safeName".withImageExtension(contentType)
            client.storage.from(HavnMediaBucket.AgentPhotos).upload(storagePath, photo.bytes) {
                upsert = true
                this.contentType = ContentType.parse(contentType)
            }
            client.from(AGENTS).update(
                AgentPhotoUpdateRow(
                    avatarDataUrl = storagePath,
                    avatarStoragePath = storagePath
                )
            ) {
                filter { eq("id", userId) }
                select()
            }.decodeSingle<AgentProfileRow>().toDomain()
                .also { agentProfileCache[userId] = it }
        }.onFailure {
            HavnLogger.error("Auth", "Failed to update agent photo $userId", it)
        }
    }

    override suspend fun logout(): Result<Unit> {
        return runCatching {
            client.auth.signOut()
        }.onFailure {
            HavnLogger.error("Auth", "Logout failed", it)
        }
    }

    private companion object {
        const val AGENTS = "agents"
        val AgentProfileColumns = Columns.list(
            "id",
            "email",
            "full_name",
            "first_name",
            "last_name",
            "avatar_url",
            "avatar_storage_path"
        )
    }
}

private fun String.sanitizeFileName(): String =
    lowercase()
        .replace(Regex("[^a-z0-9._-]+"), "-")
        .trim('-', '.', '_')
        .ifBlank { "photo.jpg" }

private fun timestampSlug(): String =
    kotlin.time.Clock.System.now().toEpochMilliseconds().toString()

private fun String.withImageExtension(contentType: String): String =
    if (substringAfterLast('/').substringAfterLast('.', "").isNotBlank()) this else "$this.${contentType.havnFileExtension()}"

private fun JsonObject.stringValue(key: String): String? =
    this[key]?.jsonPrimitive?.contentOrNull?.takeIf { it.isNotBlank() }

@Serializable
private data class AgentExistsForEmailParams(
    @SerialName("p_email") val email: String
)

@Serializable
private data class AgentProfileInsertRow(
    val id: String,
    @SerialName("first_name") val firstName: String,
    @SerialName("last_name") val lastName: String,
    @SerialName("full_name") val fullName: String,
    val email: String
)

@Serializable
private data class AgentProfileUpdateRow(
    @SerialName("first_name") val firstName: String,
    @SerialName("last_name") val lastName: String,
    @SerialName("full_name") val fullName: String
)

@Serializable
private data class AgentPhotoUpdateRow(
    @SerialName("avatar_url") val avatarDataUrl: String,
    @SerialName("avatar_storage_path") val avatarStoragePath: String
)

@Serializable
private data class AgentProfileRow(
    val id: String,
    @SerialName("first_name") val firstName: String? = null,
    @SerialName("last_name") val lastName: String? = null,
    @SerialName("full_name") val fullName: String,
    val email: String? = null,
    @SerialName("avatar_url") val avatarDataUrl: String? = null,
    @SerialName("avatar_storage_path") val avatarStoragePath: String? = null
) {
    fun toDomain(): AgentProfile {
        val first = firstName.orEmpty().ifBlank { fullName.substringBefore(" ") }
        val last = lastName.orEmpty().ifBlank { fullName.substringAfter(" ", "") }
        return AgentProfile(
            id = id,
            firstName = first,
            lastName = last,
            fullName = fullName,
            email = email,
            avatarDataUrl = havnPublicStorageSource(HavnMediaBucket.AgentPhotos, avatarStoragePath ?: avatarDataUrl)
        )
    }
}
